import { type FC } from "react";
import {
  Alert,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  EmailBlockTypeType,
  type EmailDynamicSourceType,
} from "@/types/graphql/partner.generated";
import {
  DYNAMIC_SOURCE_HINT_KEYS,
  DYNAMIC_SOURCE_LABEL_KEYS,
  DYNAMIC_SOURCES,
  EMAIL_TEMPLATE_LIMITS,
  urlVariableHintKey,
  type EditorBlock,
} from "../model/blocks";
import { ContentPicker } from "./ContentPicker";
import { ImageBlockField } from "./ImageBlockField";
import { ProductPicker } from "./ProductPicker";

interface BlockEditorProps {
  block: EditorBlock;
  onChange: (patch: Partial<EditorBlock>) => void;
  /** Переменные-адреса кнопок с сервера (marketingLimits.emailUrlVariables). */
  urlVariables?: readonly string[];
}

const NO_URL_VARIABLES: readonly string[] = [];

// Переменная в подсказке передаётся параметром: иначе i18next принял бы
// {{firstName}} в тексте перевода за свою интерполяцию.
const FIRST_NAME_VARIABLE = "{{firstName}}";

/**
 * Форма блока по типу, по ветке на каждый тип из EDITABLE_BLOCK_TYPES.
 * DIVIDER полей не имеет.
 */
export const BlockEditor: FC<BlockEditorProps> = ({
  block,
  onChange,
  urlVariables = NO_URL_VARIABLES,
}) => {
  const { t } = useTranslation();

  switch (block.type) {
    case EmailBlockTypeType.HeadingType:
      return (
        <TextField
          label={t("emailTemplates.blocks.heading")}
          value={block.text ?? ""}
          onChange={(event) => onChange({ text: event.target.value })}
          slotProps={{
            htmlInput: { maxLength: EMAIL_TEMPLATE_LIMITS.text },
          }}
          fullWidth
          autoFocus
        />
      );
    case EmailBlockTypeType.TextType:
      return (
        <TextField
          label={t("emailTemplates.blocks.text")}
          value={block.text ?? ""}
          onChange={(event) => onChange({ text: event.target.value })}
          helperText={t("emailTemplates.textHint", {
            variable: FIRST_NAME_VARIABLE,
          })}
          slotProps={{
            htmlInput: { maxLength: EMAIL_TEMPLATE_LIMITS.text },
          }}
          multiline
          minRows={3}
          fullWidth
          autoFocus
        />
      );
    case EmailBlockTypeType.ButtonType:
      return (
        <Stack spacing={2}>
          <TextField
            label={t("emailTemplates.blocks.buttonLabel")}
            value={block.label ?? ""}
            onChange={(event) => onChange({ label: event.target.value })}
            slotProps={{
              htmlInput: { maxLength: EMAIL_TEMPLATE_LIMITS.label },
            }}
            fullWidth
            autoFocus
          />
          <TextField
            label={t("emailTemplates.blocks.url")}
            value={block.url ?? ""}
            onChange={(event) => onChange({ url: event.target.value })}
            helperText={t("emailTemplates.blocks.urlHint")}
            placeholder="https://"
            slotProps={{
              htmlInput: { maxLength: EMAIL_TEMPLATE_LIMITS.url },
            }}
            fullWidth
          />
          {urlVariables.length > 0 && (
            <Stack spacing={0.5}>
              <Typography variant="caption" color="text.secondary">
                {t("emailTemplates.blocks.urlVariablesHint")}
              </Typography>
              {urlVariables.map((name) => (
                <Typography key={name} variant="caption" color="text.secondary">
                  {t(urlVariableHintKey(name), {
                    variable: `{{${name}}}`,
                  })}
                </Typography>
              ))}
            </Stack>
          )}
        </Stack>
      );
    case EmailBlockTypeType.ImageType:
      return <ImageBlockField block={block} onChange={onChange} />;
    case EmailBlockTypeType.ContentCardType:
      return (
        <ContentPicker
          value={block.contentId ?? null}
          onChange={(contentId) => onChange({ contentId })}
        />
      );
    case EmailBlockTypeType.ProductCardType:
      return (
        <ProductPicker
          value={block.productId ?? null}
          onChange={(productId) => onChange({ productId })}
        />
      );
    case EmailBlockTypeType.DynamicCardType:
      return (
        <Stack spacing={2}>
          <TextField
            select
            label={t("emailTemplates.blocks.source")}
            value={block.source ?? ""}
            onChange={(event) =>
              onChange({
                source: event.target.value as EmailDynamicSourceType,
              })
            }
            error={!block.source}
            helperText={
              block.source
                ? t(DYNAMIC_SOURCE_HINT_KEYS[block.source])
                : t("emailTemplates.blocks.sourceHint")
            }
            fullWidth
            autoFocus
          >
            {DYNAMIC_SOURCES.map((source) => (
              <MenuItem key={source} value={source}>
                {t(DYNAMIC_SOURCE_LABEL_KEYS[source])}
              </MenuItem>
            ))}
          </TextField>
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(block.required)}
                onChange={(event) =>
                  onChange({ required: event.target.checked })
                }
              />
            }
            label={t("emailTemplates.blocks.required")}
          />
          <Alert severity="info">{t("emailTemplates.dynamicHint")}</Alert>
        </Stack>
      );
    default:
      // DIVIDER без полей.
      return null;
  }
};
